
const BAR_WIDTH = 2
const BAR_GAP = 2
export const PITCH = BAR_WIDTH + BAR_GAP

export const DEFAULT_DIM = 0.3

// dim says how much of the wave is left showing before the progress reaches it: a wave over a full
// bubble of its own colour needs more than one over a plain background.
export function drawWaveform(canvas, peaks, progress = 0, { align = "left", offsetX = 0, dim = DEFAULT_DIM } = {}) {
  const context = canvas.getContext("2d")
  const dpr = window.devicePixelRatio || 1
  const width = canvas.clientWidth || 160
  const height = canvas.clientHeight || 28
  if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
    canvas.width = width * dpr
    canvas.height = height * dpr
  }
  context.setTransform(dpr, 0, 0, dpr, 0, 0)
  context.clearRect(0, 0, width, height)
  if (!peaks || peaks.length === 0) return

  const slots = Math.max(1, Math.floor(width / PITCH))
  const bars = align === "right" ? Math.min(slots, peaks.length) : slots
  const values = align === "right" ? peaks.slice(-bars) : bucketMax(peaks, bars)
  const startX = (align === "right" ? width - BAR_WIDTH - (bars - 1) * PITCH : 0) - offsetX
  const minHeight = Math.max(3, height * 0.12)
  const playedBars = Math.round(bars * Math.min(Math.max(progress, 0), 1))

  context.fillStyle = getComputedStyle(canvas).color
  for (let i = 0; i < bars; i++) {
    const value = Math.min(Math.max(values[i] || 0, 0), 1)
    const barHeight = minHeight + value * (height - minHeight)
    context.globalAlpha = i < playedBars ? 1 : dim
    drawBar(context, startX + i * PITCH, (height - barHeight) / 2, BAR_WIDTH, barHeight)
  }
  context.globalAlpha = 1
}

export function downsamplePeaks(peaks, bins) {
  if (!peaks || peaks.length === 0) return []
  return normalizePeaks(bucketMax(peaks, bins))
}

export function placeholderPeaks(bins = 48) {
  const peaks = []
  for (let i = 0; i < bins; i++) {
    const wave = Math.sin(i * 0.5) * 0.5 + Math.sin(i * 1.3) * 0.3
    peaks.push(0.35 + Math.abs(wave) * 0.5)
  }
  return peaks
}

function bucketMax(peaks, bins) {
  if (peaks.length <= bins) {
    const values = []
    for (let i = 0; i < bins; i++) values.push(peaks[Math.floor((i / bins) * peaks.length)] || 0)
    return values
  }
  const values = []
  for (let i = 0; i < bins; i++) {
    const start = Math.floor((i / bins) * peaks.length)
    const end = Math.max(start + 1, Math.ceil(((i + 1) / bins) * peaks.length))
    let max = 0
    for (let j = start; j < end && j < peaks.length; j++) {
      if (peaks[j] > max) max = peaks[j]
    }
    values.push(max)
  }
  return values
}

function normalizePeaks(peaks) {
  let max = 0
  for (const peak of peaks) {
    if (peak > max) max = peak
  }
  if (max < 0.03) return peaks.map(() => 0)
  return peaks.map((peak) => Math.pow(peak / max, 0.7))
}

function drawBar(context, x, y, width, height) {
  context.beginPath()
  if (context.roundRect) {
    context.roundRect(x, y, width, height, width / 2)
  } else {
    context.rect(x, y, width, height)
  }
  context.fill()
}

// A file picked from the device carries no live levels: its wave comes from the decoded samples.
export function peaksFromAudioBuffer(buffer, bins = 64) {
  const samples = buffer.getChannelData(0)
  const size = Math.max(1, Math.floor(samples.length / bins))
  const peaks = []
  for (let i = 0; i < bins; i++) {
    let max = 0
    const end = Math.min(samples.length, (i + 1) * size)
    for (let j = i * size; j < end; j++) {
      const value = Math.abs(samples[j])
      if (value > max) max = value
    }
    peaks.push(max)
  }
  return normalizePeaks(peaks)
}
